<template>
  <div>
    <div class="form-container mt-4">
      <div class="Classification-title">Classification Results</div>

      <div class="classification-background">
        <!-- Face Region -->
        <div class="classification-row">
          <div
            v-for="region in ['FACE', 'MALAR', 'PHILTRUM', 'MOUTH']"
            :key="region"
            class="classification-section"
          >
            <div class="region-content col-10">
              <div class="d-flex align-items-center col-8 px-0">
                <div class="d-flex flex-column align-items-center">
                  <div class="region-title py-2">
                    <strong>{{ region }}</strong>
                  </div>
                  <img
                    :src="
                      classificationResults[region]?.image ||
                      'fallback-image.jpg'
                    "
                    :alt="region"
                    class="region-image"
                  />
                </div>
                <div class="classification-info mx-1">
                  <strong>Mean Value: </strong>
                  <span
                    :class="
                      classificationResults[region]?.meanvalue ===
                      'Not Available'
                        ? 'text-smaller'
                        : ''
                    "
                  >
                    {{
                      classificationResults[region]?.meanvalue ===
                      "Not Available"
                        ? "Not Available"
                        : classificationResults[region]?.meanvalue?.toFixed(
                            4,
                          ) || "N/A"
                    }}
                  </span>
                  <br />
                  <strong>Standard Deviation: </strong>
                  <span
                    :class="
                      classificationResults[region]?.stderror ===
                      'Not Available'
                        ? 'text-smaller'
                        : ''
                    "
                  >
                    {{
                      classificationResults[region]?.stderror ===
                      "Not Available"
                        ? "Not Available"
                        : classificationResults[region]?.stderror?.toFixed(4) ||
                          "N/A"
                    }}
                  </span>
                </div>
              </div>

              <!-- Classification Info -->
              <div
                class="d-flex justify-content-center align-items-center col-4 px-0"
              >
                <div
                  v-if="
                    classificationResults[region]?.meanvalue !== 'Not Available'
                  "
                  ref="gaugeChart"
                  :id="'gauge-chart-' + region"
                  class="gauge-chart"
                ></div>
                <div v-else class="gauge-unavailable">
                  Gauge chart is not available.
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- General Overview -->
        <div class="general-overview">
          <div class="Classification-title">
            Classification Results Overview
          </div>
          <div
            class="overview-container w-100 d-flex justify-content-center align-items-center h-100"
          >
            <div ref="radarChart" class="radar-chart mx-0"></div>
            <div class="legend-box h-100">
              <div class="legend-item">
                <div class="legend-sphere mean"></div>
                <span>Mean Values</span>
              </div>
              <div class="legend-item">
                <div class="legend-rect green"></div>
                <span>Control</span>
              </div>
              <div class="legend-item">
                <div class="legend-rect red"></div>
                <span>PAE-associated</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import html2canvas from "html2canvas";
import * as echarts from "echarts";
export default {
  props: {
    chosenLandmarkId: Number,
    pflAnalysisResult: Object,
    arcAnalysisResult: {
      type: Object,
      default: () => ({ mandible: null, maxillary: null }),
    },
  },
  data() {
    return {
      riskPulseTimer: null,
      classificationResults: {
        FACE: {
          meanvalue: null,
          stderror: null,
          image: require("../../../assets/face.jpg"),
        },
        MALAR: {
          meanvalue: null,
          stderror: null,
          image: require("../../../assets/malar.jpg"),
        },
        PHILTRUM: {
          meanvalue: null,
          stderror: null,
          image: require("../../../assets/philtrum.jpg"),
        },
        MOUTH: {
          meanvalue: null,
          stderror: null,
          image: require("../../../assets/mouth.jpg"),
        },
      },
      loading: false,
    };
  },
  mounted() {
    this.initializeGaugeCharts();
  },

  methods: {
    arcIsRisky(rawArc) {
      if (!rawArc) return false;
      if (rawArc.percentile != null) return rawArc.percentile < 10;
      if (rawArc.zScore != null) return rawArc.zScore < -1.281551565545;
      return false;
    },

    getArcDisplayValue(rawArc) {
      if (!rawArc || rawArc.zScore == null || Number.isNaN(rawArc.zScore))
        return null;
      return this.nrmalizeArczScore(rawArc.zScore);
    },
    nrmalizeArczScore(z) {
      const ARC_CUTOFF_Z = -1.281551565545;
      if (z == null || Number.isNaN(z)) return null;
      return Math.max(-2, Math.min(2, ARC_CUTOFF_Z - z));
    },
    normalizePFLzScore(zScore) {
      // clamp input
      const z = Math.max(-2.326, Math.min(zScore, 2.326));
      const cutoff = -1.28;
      if (z <= cutoff) {
        const span = cutoff - -2.326;
        return 2 - 2 * ((z - -2.326) / span);
      } else {
        const span = 2.326 - cutoff;
        return -2 * ((z - cutoff) / span);
      }
    },
    async renderFromCache(result) {
      const clone = JSON.parse(JSON.stringify(result));
      for (const region of Object.keys(this.classificationResults)) {
        if (clone[region]) {
          this.classificationResults[region].meanvalue =
            clone[region].meanvalue ?? null;
          this.classificationResults[region].stderror =
            clone[region].stderror ?? null;
        }
      }
      this.$nextTick(() => {
        this.initializeGaugeCharts();
        this.initializeRadarChart();
        this.captureSnapshot().catch(() => {});
      });
    },

    getResults() {
      return JSON.parse(JSON.stringify(this.classificationResults));
    },
    async captureSnapshot() {
      try {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        const classificationElement = this.$el.querySelector(
          ".classification-background",
        );
        if (!classificationElement) {
          console.error("Classification section not found.");
          return;
        }
        const canvas = await html2canvas(classificationElement, {
          scale: 4,
          useCORS: true,
          backgroundColor: null,
        });
        const snapshot = canvas.toDataURL("image/JPEG", 1.5);
        this.$emit("classificationSnapshotCaptured", snapshot);
      } catch (error) {
        console.error("Error capturing classification snapshot:", error);
      }
    },
    async fetchExistingClassifications(landmarkId) {
      const fasdBackendURL =
        localStorage.getItem("fasdBackendURL") || "http://localhost:8000";
      const token = localStorage.getItem("userJWtoken");

      const response = await fetch(
        `${fasdBackendURL}/api/meanClassifications/${landmarkId}`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!response.ok) return null;

      const data = await response.json();
      return Array.isArray(data) && data.length ? data : null;
    },

    async fetchClassifications() {
      this.loading = true;

      const regions = ["FACE", "MALAR", "PHILTRUM", "MOUTH"];
      const fasdBackendURL =
        localStorage.getItem("fasdBackendURL") || "http://localhost:8000";
      const token = localStorage.getItem("userJWtoken");
      const result = {};

      const ClassifcationResults = await this.fetchExistingClassifications(
        this.chosenLandmarkId,
      );
      if (ClassifcationResults) {
        // Fill UI state from existing array
        for (const row of ClassifcationResults) {
          const r = (row.facialregion || "").toUpperCase();
          if (regions.includes(r)) {
            this.classificationResults[r].meanvalue = row.meanvalue;
            this.classificationResults[r].stderror = row.stderror;
            result[r] = { meanvalue: row.meanvalue, stderror: row.stderror };
            this.$emit(
              "regionFetched",
              `${r} Facial Region fetched successfully.`,
            );
            this.$emit("classificationMeansUpdated", {
              FACE: this.classificationResults.FACE?.meanvalue ?? null,
              MALAR: this.classificationResults.MALAR?.meanvalue ?? null,
              PHILTRUM: this.classificationResults.PHILTRUM?.meanvalue ?? null,
              MOUTH: this.classificationResults.MOUTH?.meanvalue ?? null,
            });
          }
        }
        // initialize charts and finish
        this.initializeGaugeCharts();
        this.$nextTick(() => this.initializeRadarChart());
        this.loading = false;
        return JSON.parse(JSON.stringify(result));
      }

      for (const region of regions) {
        try {
          const response = await fetch(
            `${fasdBackendURL}/api/meanClassifications/compute/${this.chosenLandmarkId}?facialregion_code=${region}`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            },
          );

          if (!response.ok) {
            throw new Error(`Error ${response.status}: ${response.statusText}`);
          }

          const data = await response.json();
          this.classificationResults[region].meanvalue = data.meanvalue;
          this.classificationResults[region].stderror = data.stderror;

          result[region] = {
            meanvalue: data.meanvalue,
            stderror: data.stderror,
          };

          // Emit the Success message
          this.$emit(
            "regionFetched",
            `${region} Facial Region fetched successfully.`,
          );
        } catch (error) {
          console.error(`Error fetching classification for ${region}:`, error);
          this.classificationResults[region].meanvalue = "Not Available";
          this.classificationResults[region].stderror = "Not Available";
          this.classificationResults[region].error =
            "Service is not available, and the value is set to 'Not Available'.";
        }
        this.$emit("classificationMeansUpdated", {
          FACE: this.classificationResults.FACE?.meanvalue ?? null,
          MALAR: this.classificationResults.MALAR?.meanvalue ?? null,
          PHILTRUM: this.classificationResults.PHILTRUM?.meanvalue ?? null,
          MOUTH: this.classificationResults.MOUTH?.meanvalue ?? null,
        });
        // Delay
        await new Promise((resolve) => setTimeout(resolve, 900));
      }

      this.initializeGaugeCharts();
      this.$nextTick(() => this.initializeRadarChart());

      this.loading = false;
      return JSON.parse(JSON.stringify(result));
    },

    initializeGaugeCharts() {
      this.$nextTick(() => {
        Object.keys(this.classificationResults).forEach((region) => {
          const chartDom = document.getElementById(`gauge-chart-${region}`);
          if (!chartDom) {
            console.error(`DOM not found for region: ${region}`);
            return;
          }

          const MeanValue = this.classificationResults[region].meanvalue;
          if (MeanValue === "Not Available") {
            console.log(`Gauge chart not available for region: ${region}`);
            return;
          }

          const chart = echarts.init(chartDom);
          const meanValue = Math.max(
            -2,
            Math.min(this.classificationResults[region].meanvalue || 0, 2),
          );

          const isPhiltrum = region === "PHILTRUM";
          const label =
            meanValue >= 0 && meanValue <= 2
              ? isPhiltrum
                ? "Smooth"
                : "PAE-associated"
              : meanValue >= -2 && meanValue < 0
              ? isPhiltrum
                ? "Not Smooth"
                : "Control"
              : "";

          const labelColor = isPhiltrum
            ? label === "Smooth"
              ? "#FF3B3B"
              : "#00BFA5"
            : label === "PAE-associated"
            ? "#FF3B3B"
            : "#00BFA5";

          const option = {
            series: [
              {
                type: "gauge",
                startAngle: 180,
                endAngle: 0,
                center: ["50%", "75%"],
                radius: "110%",
                min: -2,
                max: 2,
                splitNumber: 4,
                axisLine: {
                  lineStyle: {
                    width: 4,
                    color: [
                      [0.5, "#00BFA5"],
                      [1, "#FF3B3B"],
                    ],
                  },
                },
                pointer: {
                  icon: "path://M12.8,0.7l12,40.1H0.7L12.8,0.7z",
                  length: "13%",
                  width: 9,
                  offsetCenter: [0, "-55%"],
                  itemStyle: {
                    color: "auto",
                  },
                },
                axisTick: {
                  length: 5,
                  lineStyle: {
                    color: "auto",
                    width: 1,
                  },
                },
                splitArea: {
                  areaStyle: {
                    color: [
                      "rgba(16, 185, 129, 0.32)",
                      "rgba(16, 185, 129, 0.52)",
                      "rgba(220, 38, 38, 0.42)",
                      "rgba(220, 38, 38, 0.68)",
                    ],
                    shadowColor: "rgba(220, 38, 38, 0.22)",
                    shadowBlur: 20,
                  },
                },
                axisLabel: {
                  color: "#464646",
                  fontSize: 12,
                  formatter: function (value) {
                    if (isPhiltrum) {
                      if (value === -1) {
                        return "{notSmooth|Not Smooth}";
                      } else if (value === 1) {
                        return "{smooth|Smooth}";
                      }
                    } else {
                      if (value === -1) {
                        return "{control|Control}";
                      } else if (value === 1) {
                        return "{fas|FAS}";
                      }
                    }
                    if (value === 0) {
                      return "\n0";
                    } else if (value === -2) {
                      return "{minustwo|-2}";
                    } else if (value === 2) {
                      return "{two|2}";
                    }
                    return "";
                  },
                  rich: {
                    notSmooth: {
                      color: "#00BFA5",
                      align: "center",
                      fontSize: 12,
                      padding: [0, 250, 0, -35],
                    },
                    smooth: {
                      color: "#FF3B3B",
                      align: "center",
                      fontSize: 12,
                      padding: [0, 0, 15, 0],
                    },
                    control: {
                      color: "#00BFA5",
                      align: "center",
                      fontSize: 13,
                      padding: [0, 18, 0, -16],
                    },
                    fas: {
                      color: "#FF3B3B",
                      align: "center",
                      fontSize: 12,
                    },
                    minustwo: {
                      color: "#464646",
                      fontSize: 12,
                      padding: [0, 0, 5, 7],
                    },
                    two: {
                      color: "#464646",
                      fontSize: 12,
                      padding: [0, 11, 5, 0],
                    },
                  },
                  distance: -45,
                },
                title: {
                  offsetCenter: [0, "-1%"],
                  fontSize: 13,
                  color: labelColor,
                },
                detail: {
                  fontSize: 12,
                  offsetCenter: [0, "-19%"],
                  valueAnimation: true,
                  formatter: function (value) {
                    return value.toFixed(2);
                  },
                  color: "inherit",
                },
                data: [
                  {
                    value: meanValue,
                    name: label,
                  },
                ],
              },
            ],
          };

          chart.setOption(option);
        });
        this.$nextTick(() => {
          this.captureSnapshot();
        });
      });
    },

    initializeRadarChart() {
      const pflzScore =
        this.pflAnalysisResult?.PFLzScore == null
          ? null
          : this.normalizePFLzScore(this.pflAnalysisResult.PFLzScore);

      const chartDom = this.$refs.radarChart;
      if (!chartDom) return;

      const prev = echarts.getInstanceByDom(chartDom);
      if (prev) prev.dispose();

      const chart = echarts.init(chartDom);

      const mandibleRaw = this.arcAnalysisResult?.mandible;
      const maxillaryRaw = this.arcAnalysisResult?.maxillary;

      const mandibleZ =
        mandibleRaw?.zScore != null
          ? this.nrmalizeArczScore(mandibleRaw.zScore)
          : null;

      const maxillaryZ =
        maxillaryRaw?.zScore != null
          ? this.nrmalizeArczScore(maxillaryRaw.zScore)
          : null;

      const mandibleRisky = this.arcIsRisky(mandibleRaw);
      const maxillaryRisky = this.arcIsRisky(maxillaryRaw);
      const anyRiskyArc = mandibleRisky || maxillaryRisky;

      const meanValues = [
        this.classificationResults.FACE.meanvalue !== "Not Available"
          ? Math.max(-2, Math.min(this.classificationResults.FACE.meanvalue, 2))
          : null,
        this.classificationResults.PHILTRUM.meanvalue !== "Not Available"
          ? Math.max(
              -2,
              Math.min(this.classificationResults.PHILTRUM.meanvalue, 2),
            )
          : null,
        this.classificationResults.MOUTH.meanvalue !== "Not Available"
          ? Math.max(
              -2,
              Math.min(this.classificationResults.MOUTH.meanvalue, 2),
            )
          : null,
        this.classificationResults.MALAR.meanvalue !== "Not Available"
          ? Math.max(
              -2,
              Math.min(this.classificationResults.MALAR.meanvalue, 2),
            )
          : null,
        pflzScore !== null ? Math.max(-2, Math.min(pflzScore, 2)) : null,
        mandibleZ,
        maxillaryZ,
      ];

      const filteredMeanValues = meanValues.map((value) =>
        value !== null ? value : undefined,
      );

      const PflPercentile = this.pflAnalysisResult?.PFLpercentile;

      const thresholdRing = Array(7).fill(0);

      const option = {
        backgroundColor: "#ffffff",

        tooltip: {
          trigger: "item",
          backgroundColor: "rgba(255,255,255,0.98)",
          borderColor: "#d0d7e2",
          borderWidth: 1,
          borderRadius: 12,
          padding: [10, 14],
          textStyle: {
            color: "#1f2937",
            fontSize: 13,
          },
          extraCssText:
            "box-shadow:0 10px 30px rgba(15,23,42,0.12);backdrop-filter:blur(6px);",
          formatter: (params) => {
            const values = params.value || [];
            return `
          <div style="line-height:1.55">
            <div style="font-weight:700;font-size:14px;margin-bottom:6px;color:#004ead">${
              params.seriesName
            }</div>
            <div>Face: ${
              values[0] !== undefined ? values[0].toFixed(2) : "Not Available"
            }</div>
            <div>Philtrum: ${
              values[1] !== undefined ? values[1].toFixed(2) : "Not Available"
            }</div>
            <div>Mouth: ${
              values[2] !== undefined ? values[2].toFixed(2) : "Not Available"
            }</div>
            <div>Malar: ${
              values[3] !== undefined ? values[3].toFixed(2) : "Not Available"
            }</div>
            <div>PFL Percentile: ${
              PflPercentile != null ? PflPercentile.toFixed(2) : "Not Available"
            }</div>
            <div>Mandible: ${
              values[5] !== undefined ? values[5].toFixed(2) : "Not Available"
            }</div>
            <div>Maxilla: ${
              values[6] !== undefined ? values[6].toFixed(2) : "Not Available"
            }</div>
          </div>
        `;
          },
        },

        radar: {
          shape: "polygon",
          center: ["50%", "50%"],
          radius: 160,
          startAngle: 90,
          splitNumber: 4,
          indicator: [
            { name: "FACE", max: 2, min: -2 },
            { name: "PHILTRUM", max: 2, min: -2 },
            { name: "MOUTH", max: 2, min: -2 },
            { name: "MALAR", max: 2, min: -2 },
            { name: "PFL", max: 2, min: -2 },
            { name: "MANDIBLE", max: 2, min: -2 },
            { name: "MAXILLA", max: 2, min: -2 },
          ],

          axisName: {
            color: "#2f3b52",
            fontWeight: "700",
            fontSize: 13,
            padding: [2, 2],
          },

          axisLine: {
            lineStyle: {
              color: "rgba(203, 213, 225, 0.95)",
              width: 1.2,
            },
          },

          splitLine: {
            lineStyle: {
              color: "rgba(203, 213, 225, 0.9)",
              width: 1,
            },
          },

          splitArea: {
            areaStyle: {
              color: [
                "rgba(16, 185, 129, 0.22)",
                "rgba(16, 185, 129, 0.54)",
                "rgba(220, 38, 38, 0.28)",
                "rgba(220, 38, 38, 0.55)",
              ],
              shadowColor: "rgba(220, 38, 38, 0.28)",
              shadowBlur: 20,
            },
          },
        },

        series: [
          {
            name: "Threshold Ring",
            type: "radar",
            silent: true,
            z: 1,
            data: [
              {
                value: thresholdRing,
                name: "Threshold Ring",
                symbol: "none",
                lineStyle: {
                  color: "rgba(16, 185, 129, 0.3)",
                  width: 3.2,
                  type: "solid",
                  shadowColor: "rgba(16, 185, 129, 0.2)",
                  shadowBlur: 12,
                },
                itemStyle: {
                  color: "rgba(16, 185, 129, 0.35)",
                },
                areaStyle: {
                  color: "rgba(255,255,255,0)",
                },
              },
            ],
          },
         /* {
            name: "Outer Border",
            type: "radar",
            silent: true,
            z: 1,
            data: [
              {
                value: Array(7).fill(2),
                symbol: "none",
                lineStyle: {
                  color: "rgba(220, 38, 38, 0.4)",
                  width: 3.2,
                  shadowColor: "rgba(220, 38, 38, 0.7)",
                  shadowBlur: 12,
                },
                itemStyle: {
                  color: "rgba(220, 38, 38, 0.8)",
                },
                areaStyle: {
                  color: "rgba(255,255,255,0)",
                },
              },
            ],
          }*/

          {
            name: "Mean Values",
            type: "radar",
            z: 3,
            data: [
              {
                value: filteredMeanValues,
                name: "Mean Values",
                symbol: "circle",
                symbolSize: 7,
                itemStyle: {
                  color: "#004ead",
                  borderColor: "#transparent",
                  shadowColor: "rgba(11, 94, 215, 0.25)",
                  shadowBlur: 8,
                },
                lineStyle: {
                  color: "#004ead",
                  width: 2.5,
                },
                areaStyle: {
                  color: new echarts.graphic.RadialGradient(0.5, 0.5, 1, [
                    { offset: 0, color: "rgba(59, 130, 246, 0.22)" },
                    { offset: 1, color: "rgba(59, 130, 246, 0.36)" },
                  ]),
                },
                emphasis: {
                  lineStyle: {
                    color: "#004ead",
                    width: 3.2,
                    shadowColor: "rgba(59, 130, 246, 0.35)",
                    shadowBlur: 14,
                  },
                  itemStyle: {
                    color: "#0b5ed7",
                    shadowColor: "rgba(59, 130, 246, 0.5)",
                    shadowBlur: 14,
                  },
                  areaStyle: {
                    color: new echarts.graphic.RadialGradient(0.5, 0.5, 1, [
                      { offset: 0, color: "rgba(59, 130, 246, 0.22)" },
                      { offset: 1, color: "rgba(59, 130, 246, 0.36)" },
                    ]),
                  },
                },
              },
            ],
          },
          {
            name: "Risk Glow",
            type: "radar",
            silent: true,
            z: 2,
            data: [
              {
                value: anyRiskyArc
                  ? [2, 2, 2, 2, 2, 2, 2]
                  : [0, 0, 0, 0, 0, 0, 0],
                symbol: "none",
                lineStyle: {
                  color: anyRiskyArc
                    ? "rgba(239, 68, 68, 0.55)"
                    : "rgba(0,0,0,0)",
                  width: 10,
                  shadowColor: "rgba(239, 68, 68, 0.45)",
                  shadowBlur: 22,
                },
                areaStyle: {
                  color: anyRiskyArc
                    ? new echarts.graphic.RadialGradient(0.5, 0.5, 1, [
                        { offset: 0, color: "rgba(239, 68, 68, 0.04)" },
                        { offset: 1, color: "rgba(239, 68, 68, 0.22)" },
                      ])
                    : "rgba(0,0,0,0)",
                },
              },
            ],
          },
        ],
      };

      chart.setOption(option);

      this.$nextTick(() => {
        this.captureSnapshot();
      });
    },
  },
  watch: {
    arcAnalysisResult: {
      deep: true,
      immediate: true,
      handler() {
        this.$nextTick(() => {
          this.initializeRadarChart();
        });
      },
    },
  },
};
</script>

<style>
.Classification-title {
  text-align: left;
  margin-left: 15px;
  font-size: 1.2em;
  color: #212529;
  font-weight: bold;
  padding: 30px;
}
.classification-row {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
}
.classification-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}
.region-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.region-title {
  font-size: 1.1em;
  color: #212529;
  /* text-align: left; */
}
.region-image {
  width: 160px;
  height: auto;
  margin-right: 10px;
}
.classification-info {
  font-size: 1.2em;
  color: #212529;
  margin-right: 15px;
  text-align: left;
}
.gauge-chart {
  width: 300px;
  height: 200px;
  margin-left: 60px;
}
.region-image,
.gauge-chart {
  transition: transform 0.3s ease;
}
.region-image:hover,
.gauge-chart:hover {
  transform: translateY(-10px);
}
.general-overview {
  margin-top: 30px;
  text-align: left;
}
.radar-chart {
  width: 600px;
  height: 400px;
  margin: 0 auto;
}
.radar-chart-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 20px;
}
.form-container-Classification {
  margin-top: 10px;
  padding: 10px;
  background-color: #f7f7f7c5;
  border-radius: 8px;
  margin-left: 40px;
  margin-right: 40px;
}
.overview-container {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}
.legend-box {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-left: 20px;
}
.legend-item {
  display: flex;
  align-items: center;
  gap: 10px;
}
.legend-rect {
  width: 20px;
  height: 20px;
  border-radius: 4px;
}
.legend-rect.green {
  background-color: rgb(16, 185, 129);
}

.legend-rect.red {
  background-color: rgb(220, 38, 38);
  opacity: 1;
}
.legend-sphere {
  width: 15px;
  height: 15px;
  border-radius: 50%;
}
.legend-sphere.mean {
  background-color: #004ead;
}
.legend-item span {
  font-size: 14px;
  color: #212529;
  font-weight: bold;
}
.classification-background {
  background-color: #ffffff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  margin: 30px;
}
.gauge-unavailable {
  text-align: center;
  color: gray;
  font-style: italic;
  font-size: 14px;
  margin-top: 10px;
}
.text-smaller {
  font-size: 14px;
  color: #959595;
}
</style>
